import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Copy, Download, RectangleHorizontal, Smartphone, Square } from 'lucide-react';
import { StillProvider } from '../components/alt/stillContext';
import { PlateArtboard, PlatePreview, VIEW_SIZES, type PlateView } from '../components/exports/CreatorFrame';
import { CREATOR_ASSETS } from '../components/exports/creatorAssets';
import { BRAND_ASSETS, EndCard } from '../components/exports/BrandMarks';
import { TOOL_PILLS, CRM_PILLS, SURFACE_PILLS } from '../components/alt/SurfacePill';
import { SurfacePills } from '../components/exports/SurfacePills';
import { useExportFrames } from '../components/exports/useExportFrames';
import { EXPORT_META } from '../components/exports/exportUi';
import { VIDEO_CLOSERS } from '../components/exports/videoClosers';

/**
 * THE CREATOR PACK, ON ITS OWN PAGE.
 *
 * It began as a section at the bottom of the screen library, which put the
 * vertical plates behind twenty landscape frames — the wrong order for the one
 * person it is for. Someone cutting a clip has a single job and should land on
 * the plates, in story order, with nothing else to scroll past.
 *
 * Three kinds of asset live here now, in the order they get reached for:
 * product plates, brand marks, and the two overlays every clip needs.
 *
 * The export machinery is shared rather than duplicated: `useExportFrames`
 * carries the clipboard fallback, the font-and-logo wait and the busy state
 * for this page and the screen library both.
 */

/*
 * A RATIO TOGGLE used to sit here, for the still closer. Both shapes are now
 * their own card, so there is nothing left to toggle between.
 */

/**
 * WHICH OF THE THREE DELIVERABLES YOU ARE TAKING. Not a preview switch — the
 * export buttons follow it.
 */
function ViewToggle({
  value,
  onChange,
  label
}: {
  value: PlateView;
  onChange: (v: PlateView) => void;
  label: string;
}) {
  return <div role="group" aria-label={label} className="inline-flex items-center rounded-lg border border-[#D8CFC2] bg-white p-1" data-id="element-8087" data-mp="vzosxuopg3gw">
      {([['overlay', 'Overlay', Square], ['9:16', '9:16', Smartphone], ['16:9', '16:9', RectangleHorizontal]] as const).map(([v, text, Icon]) => <button key={v} type="button" onClick={() => onChange(v)} aria-pressed={value === v} className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px] font-bold transition-colors ${value === v ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52] hover:text-[#2B211A]'}`} data-id="element-8088" data-mp="34u8i7e1e71ny">
          <Icon size={12} data-id="element-8089" data-mp="2h4e6x0wq1l5t" />
          {text}
        </button>)}
    </div>;
}
/**
 * The fixed box every preview is fitted into, whatever shape is selected. Tall
 * enough to show a 9:16 plate whole, wide enough for a 16:9 one, and never
 * smaller than a square shown at true size — so switching shapes moves the
 * plate and nothing else.
 */
const STAGE = {
  w: 620,
  h: 640
};

/** Checkerboard, so the artboard's transparency is visible rather than claimed. */
const CHECKS: React.CSSProperties = {
  backgroundImage: 'linear-gradient(45deg,#EFEAE1 25%,transparent 25%,transparent 75%,#EFEAE1 75%),linear-gradient(45deg,#EFEAE1 25%,transparent 25%,transparent 75%,#EFEAE1 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 8px 8px',
  backgroundColor: '#FFFFFF'
};
export function CreatorPack() {
  const {
    register,
    busy,
    copied,
    warm,
    copy,
    savePng,
    saveAllPng
  } = useExportFrames();
  /**
   * VIEW PICKS THE DELIVERABLE, NOT JUST THE PREVIEW.
   *
   * Each screen ships three ways — a square transparent overlay, a full 9:16
   * plate and a full 16:9 plate — because an overlay and a full-frame shot are
   * genuinely different assets. The toggle changes the file you get.
   */
  const [packView, setPackView] = useState<PlateView>('9:16');
  const [views, setViews] = useState<Record<string, PlateView>>({});
  const viewFor = (id: string) => views[id] ?? packView;
  const setAll = (v: PlateView) => {
    setPackView(v);
    // Clear the per-plate choices, or "set all" would visibly skip the plates
    // someone had already switched by hand.
    setViews({});
  };
  /*
   * HOW CLOSE THE OVERLAY SITS.
   *
   * Only meaningful on the square: the fitted overlay shows the whole screen,
   * which is right when it fills the frame and useless when it is placed at a
   * quarter size in a corner — at that scale a whole app view is texture, not
   * information. "Close" renders the same screen 1.6× larger and lets the
   * square clip it, so what is in frame can actually be read.
   */
  const [zoomed, setZoomed] = useState<Record<string, boolean>>({});
  return <main className="min-h-screen w-full bg-[#F4F1EA] pb-24" data-id="element-8090" data-mp="skdrk9k7u63d">
      <header className="border-b border-[#E7E0D4] bg-[#FAF8F4]" data-id="element-8091" data-mp="xhxfk16ptx8i">
        <div className="mx-auto max-w-[1240px] px-6 py-10" data-id="element-8092" data-mp="xg2yixykep9p">
          <Link to="/app-screen-mockups/export-dashboards" className="inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#6B5D52] transition-colors hover:text-[#2B211A]" data-id="element-8093" data-mp="ily3ygkedeym">
            <ArrowLeft size={13} data-id="element-8094" data-mp="3xfvutvz27hl" />
            All product screens
          </Link>

          <p className="mt-6 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-8095" data-mp="3w2a2h08x1yrl">
            For video
          </p>
          <h1 className="font-title mt-3 text-3xl font-semibold tracking-tight text-[#2B211A] md:text-[40px]" data-id="element-8096" data-mp="dx6qqwog2oi7">
            Creator pack
          </h1>
          <p className="mt-3 max-w-[64ch] text-[15.5px] leading-relaxed text-[#6B5D52]" data-id="element-8097" data-mp="mx27dh0rrbpm">
            Every product screen ships three ways: a square{' '}
            {VIEW_SIZES.overlay.out} overlay on a transparent artboard for
            dropping over your own footage, and full {VIEW_SIZES['9:16'].out}{' '}
            and {VIEW_SIZES['16:9'].out} plates that already sit on a
            video-looking ground, for cutting straight to. The vertical plates
            crop into the part of a screen that carries the point rather than
            shrinking the whole thing, so they stay readable on a phone. Pick
            the shape on any screen and the buttons hand you that file. Every product asset
            is a still PNG with the caption zone left clear, so you type in
            your editor rather than shipping words you cannot change — the two
            rendered closers further down are the only moving files here.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-8098" data-mp="1neuaovbfwxep">
            <button type="button" onClick={() => saveAllPng(CREATOR_ASSETS.map(a => {
            const v = viewFor(a.id);
            return {
              id: a.id,
              name: `${a.id}-${v === 'overlay' ? 'square' : v.replace(':', 'x')}`
            };
          }))} disabled={busy !== null} className="inline-flex items-center gap-2 rounded-lg bg-[#EA580C] px-4 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-[#C2410C] disabled:opacity-60" data-id="element-8099" data-mp="1sdosbtxo8fx8">
              <Download size={14} data-id="element-8100" data-mp="byvwkd1enl6m" />
              {busy ? 'Exporting…' : `Download all ${CREATOR_ASSETS.length}`}
            </button>
            <ViewToggle value={packView} onChange={setAll} label="Set every shape" data-id="element-8101" data-mp="4fmpiqgdfykq" />
          </div>

          <p className="mt-4 max-w-[64ch] text-[12.5px] leading-relaxed text-neutral-500" data-id="element-8102" data-mp="3vt0tc7xtk0ka">
            Keep this window at desktop width — the product screens inside these
            plates render their desktop layout above 1024px, and the export
            captures what is actually on screen. Every plate is held on its
            finished state, so nothing arrives mid-animation.
          </p>
        </div>
      </header>

      {/* ── Product plates ───────────────────────────────────────────────────
       ROW PER SCREEN, PREVIEW FIRST.
       Stacked in a grid, every plate pushed its name, note, toggle, caption
       and both buttons above the image — so on a 960-wide landscape plate you
       scrolled through a paragraph before seeing anything, and the previews
       never lined up because the captions differ in length. The preview now
       leads, inside a stage of constant size, and the whole control set sits
       in a fixed column beside it — so the page holds still as you compare.
       ──────────────────────────────────────────────────────────────────────*/}
      <div className="mx-auto max-w-[1400px] px-6 pt-14" data-id="element-8103" data-mp="39vfxp76hqlbi">
        <div className="flex flex-col gap-14" data-id="element-8104" data-mp="39x0r2th2jy7s">
          {CREATOR_ASSETS.map(asset => {
          const view = viewFor(asset.id);
          const size = VIEW_SIZES[view];
          const overlay = view === 'overlay';
          // Never enlarge: a square at true size is already the right reading
          // size, and scaling it up would only soften it.
          const fit = Math.min(STAGE.w / size.w, STAGE.h / size.h, 1);
          const copiedNow = copied === asset.id;
          const busyNow = busy === asset.id;
          // The 9:16 plate may compose the screen differently — a narrower
          // shelf, a tighter grid — because a phone crop is a different asset,
          // not the same one shrunk.
          const body = view === '9:16' && asset.renderVertical ? asset.renderVertical() : asset.render();
          const near = overlay && !!zoomed[asset.id];
          // A single object renders a fifth smaller than a full screen would,
          // so it sits IN the frame rather than filling it.
          const rw = asset.renderWidth ?? (asset.object ? 376 : undefined);
          const plate = <StillProvider data-id="element-8105" data-mp="2ipihq40s2p8o">
                {overlay ? <PlateArtboard width={asset.width} renderWidth={rw} zoom={near ? 1.6 : 1} data-id="element-8106" data-mp="1uyms9djso64l">
                    {body}
                  </PlateArtboard> : <PlatePreview ratio={view} width={asset.width} renderWidth={rw} crop={asset.crop} centered={asset.centered ?? asset.object} data-id="element-8107" data-mp="1q0l9ktqa68tn">
                    {body}
                  </PlatePreview>}
              </StillProvider>;
          return <section key={asset.id} className="flex flex-col gap-6 border-t border-[#E7E0D4] pt-8 lg:flex-row lg:items-start lg:gap-10" data-id="element-8108" data-mp="3gao850krrez3">
                {/* A STAGE OF CONSTANT SIZE.
                 Rendering each shape at its native pixel size meant switching
                 from 16:9 to 9:16 nearly doubled the row's height and shunted
                 everything below it down the page — the layout lurched every
                 time you compared two shapes. The stage now stays put and the
                 plate is fitted inside it, so only the plate changes. */}
                <div className="flex flex-none items-center justify-center" style={{
              width: STAGE.w,
              height: STAGE.h
            }} data-id="element-8109" data-mp="125zgs23mbnj5">
                  <div className="overflow-hidden rounded-[18px] border border-[#E7E0D4]" style={{
                width: size.w * fit,
                height: size.h * fit,
                ...(overlay ? CHECKS : null)
              }} data-id="element-8110" data-mp="t6chblug8rsq">
                    <div className="origin-top-left" style={{
                  width: size.w,
                  height: size.h,
                  transform: `scale(${fit})`
                }} data-id="element-8111" data-mp="jbarenscnlp3">
                      {plate}
                    </div>
                  </div>
                </div>

                {/* THE CAPTURED NODE, at true pixel size and parked off-canvas.
                 The capture reads real bounding boxes, so it cannot live under
                 the stage's transform — a scaled ancestor would hand back a
                 scaled file. It stays mounted rather than conditional so the
                 export never depends on what is on screen.
                  `isolate` because these screens carry absolute layers that
                 would otherwise resolve against the page. */}
                <div ref={register(asset.id)} aria-hidden="true" className="pointer-events-none fixed -left-[9999px] top-0 isolate overflow-hidden" style={{
              width: size.w,
              height: size.h,
              ...(overlay ? CHECKS : null)
            }} data-id="element-8112" data-mp="2c5tm8wjyz1jy">
                  {plate}
                </div>

                <div className="min-w-0 lg:sticky lg:top-8 lg:max-w-[380px]" data-id="element-8113" data-mp="3sjaav9no7125">
                  <h2 className="font-title text-[21px] font-semibold tracking-tight text-[#2B211A]" data-id="element-8114" data-mp="1shk55zxgpoer">
                    {asset.name}
                  </h2>
                  <p className="mt-1 text-[12.5px] text-neutral-500" data-id="element-8115" data-mp="1nihc7durb8n5">
                    {asset.note}
                  </p>

                  <div className="mt-4 flex flex-wrap items-center gap-2" onPointerEnter={() => warm(asset.id)} onFocus={() => warm(asset.id)} data-id="element-8116" data-mp="21g5tyctj1znz">
                    <button type="button" onClick={() => savePng(asset.id, 2, `${asset.id}-${overlay ? 'square' : view.replace(':', 'x')}`)} disabled={busyNow} className="inline-flex items-center gap-1.5 rounded-lg bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black disabled:opacity-60" data-id="element-8117" data-mp="19sdtgwv35x2g">
                      <Download size={13} data-id="element-8118" data-mp="1drmos65ry304" />
                      {busyNow ? 'Preparing…' : `PNG ${size.out}`}
                    </button>
                    <button type="button" onClick={() => copy(asset.id)} disabled={busyNow} className="inline-flex items-center gap-1.5 rounded-lg border border-[#D8CFC2] bg-white px-3 py-2 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:border-[#2B211A] disabled:opacity-60" data-id="element-8119" data-mp="m1zxwwdalg34">
                      {copiedNow ? <Check size={13} className="text-emerald-600" data-id="element-8120" data-mp="205ev52wh6oy" /> : <Copy size={13} data-id="element-8121" data-mp="21ghv2oiuaf0" />}
                      {copiedNow ? 'Copied' : 'Copy for Figma'}
                    </button>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-8122" data-mp="uo1ccc0an2ms">
                    <ViewToggle value={view} onChange={v => setViews(m => ({
                  ...m,
                  [asset.id]: v
                }))} label={`${asset.name} shape`} data-id="element-8123" data-mp="zn44ivv6dedc" />
                    {/* Only on the square — the full plates are composed around
                     their own ground, so cropping into them would eat the
                     caption zone they exist to protect. */}
                    {overlay && <div className="inline-flex items-center gap-1 rounded-full border border-[#E7E0D4] bg-white p-1" role="group" aria-label={`${asset.name} framing`} data-id="element-8124" data-mp="3afp7c1vqyl2y">
                        {([['Fit', false], ['Close', true]] as const).map(([label, on]) => <button key={label} type="button" onClick={() => setZoomed(m => ({
                    ...m,
                    [asset.id]: on
                  }))} aria-pressed={near === on} className={`rounded-full px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${near === on ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52] hover:text-[#2B211A]'}`} data-id="element-8125" data-mp="3luk3w24jplht">
                            {label}
                          </button>)}
                      </div>}
                  </div>

                  <p className="mt-4 text-[13px] leading-relaxed text-[#6B5D52]" data-id="element-8126" data-mp="3gavvj79yyqq">
                    {asset.use}
                  </p>
                </div>
              </section>;
        })}
        </div>
      </div>

      {/* ── Rendered closers ─────────────────────────────────────────────── */}
      <div className="mx-auto mt-20 max-w-[1360px] px-6" data-id="element-8127" data-mp="2j7glnmy38smu">
        <div className="border-b border-[#E7E0D4] pb-3" data-id="element-8128" data-mp="yrhfiz3l1hes">
          <p className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-8129" data-mp="yowbc3ftapdv">
            Video
          </p>
          <h2 className="font-title mt-1.5 text-[26px] font-semibold tracking-tight text-[#2B211A]" data-id="element-8130" data-mp="18p5czwskp30n">
            The closers
          </h2>
          <p className="mt-2 max-w-[76ch] text-[13.5px] leading-relaxed text-[#6B5D52]" data-id="element-8131" data-mp="1dgx1m1s64yo5">
            The tail to end a clip on, in both shapes — handed over as the
            original MP4 rather than re-encoded here, so you drop the source
            file straight onto your timeline. The still version is beside them,
            for a thumbnail or anywhere a video cannot go.
          </p>
        </div>

        <div className="mt-8 grid gap-x-8 gap-y-10 lg:grid-cols-2" data-id="element-8132" data-mp="1wdevaoqkv8n1">
          {VIDEO_CLOSERS.map(closer => <section key={closer.id} data-id="element-8133" data-mp="3t8b682yo07y3">
              <h3 className="font-title text-[19px] font-semibold tracking-tight text-[#2B211A]" data-id="element-8134" data-mp="3t9cl6iisxel3">
                {closer.name}
              </h3>
              <p className="mt-0.5 text-[12.5px] text-neutral-500" data-id="element-8135" data-mp="3q6g4uvcbd4mb">{closer.note}</p>
              <div className="mt-4" data-id="element-8136" data-mp="3m4bimho73a9p">
                <a href={closer.src} download className="inline-flex items-center gap-1.5 rounded-lg bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black" data-id="element-8137" data-mp="3m5e7yl5vd4ni">
                  <Download size={13} data-id="element-8138" data-mp="3v2ww3yet7z3m" />
                  Download MP4
                </a>
              </div>
              <p className="mt-4 max-w-[62ch] text-[13px] leading-relaxed text-[#6B5D52]" data-id="element-8139" data-mp="cx22tnxksrmy">
                {closer.use}
              </p>
              {/* Fitted into the same stage the plates use, so a vertical and a
               landscape closer sit side by side without one towering over the
               other. Controls rather than autoplay: this is an asset to
               inspect, not a page that should start moving on its own. */}
              <div className="mt-4 flex items-center justify-center rounded-[18px] border border-[#E7E0D4] bg-[#14100D] p-3" style={{
            height: STAGE.h * 0.62
          }} data-id="element-8140" data-mp="roavy70osv4u">
                <video src={closer.src} controls playsInline preload="metadata" className="h-full w-auto rounded-lg" data-id="element-8141" data-mp="1fb67dqazo08a" />
              </div>
            </section>)}

          {/* THE STILL CLOSER, IN BOTH SHAPES.
           It used to be one card behind a ratio toggle, which quietly made the
           landscape version feel optional — and meant the two could never be
           compared. Each shape is now its own card, beside the video it
           matches, because a still closer is a deliverable in its own right
           rather than a setting on another one. */}
          {(['9:16', '16:9'] as const).map(ratio => {
          const id = `end-card-${ratio.replace(':', 'x')}`;
          const {
            w,
            h,
            out
          } = VIEW_SIZES[ratio];
          // Fitted into the same box as the players, so all four cards line up.
          const fit = Math.min(STAGE.h * 0.62 - 24, h) / h;
          return <section key={ratio} data-id="element-8142" data-mp="t43ltu9bkxbq">
                <h3 className="font-title text-[19px] font-semibold tracking-tight text-[#2B211A]" data-id="element-8143" data-mp="t30wvrvk54yg">
                  Closer still, {ratio === '9:16' ? 'vertical' : 'landscape'}
                </h3>
                <p className="mt-0.5 text-[12.5px] text-neutral-500" data-id="element-8144" data-mp="k1ayg239d5r6">
                  {ratio} · {out} · PNG
                </p>
                <div className="mt-4" onPointerEnter={() => warm(id)} onFocus={() => warm(id)} data-id="element-8145" data-mp="a4y4d33swpvw">
                  <button type="button" onClick={() => savePng(id, 2, `giga-closer-still-${ratio.replace(':', 'x')}`)} disabled={busy === id} className="inline-flex items-center gap-1.5 rounded-lg bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black disabled:opacity-60" data-id="element-8146" data-mp="5e9f7uflg4zx">
                    <Download size={13} data-id="element-8147" data-mp="3njkx771onmbj" />
                    {busy === id ? 'Preparing…' : `PNG ${out}`}
                  </button>
                </div>
                <p className="mt-4 max-w-[62ch] text-[13px] leading-relaxed text-[#6B5D52]" data-id="element-8148" data-mp="3iljggsrqbz38">
                  Exactly where the {ratio === '9:16' ? 'vertical' : 'landscape'}{' '}
                  closer lands, frozen — same ground, same face, same address.
                  For a thumbnail, a slide, or anywhere a video tail cannot go.
                </p>
                <div className="mt-4 flex items-center justify-center rounded-[18px] border border-[#E7E0D4] bg-[#14100D] p-3" style={{
              height: STAGE.h * 0.62
            }} data-id="element-8149" data-mp="3nedbb6j2jju3">
                  <div className="overflow-hidden rounded-lg" style={{
                width: w * fit,
                height: h * fit
              }} data-id="element-8150" data-mp="2uowocoe7wv59">
                    <div className="origin-top-left" style={{
                  width: w,
                  height: h,
                  transform: `scale(${fit})`
                }} data-id="element-8151" data-mp="2zn7t4kpnofgm">
                      <EndCard ratio={ratio} data-id="element-8152" data-mp="26zc1huey1dqp" />
                    </div>
                  </div>
                </div>
                {/* Captured at true size, parked off-canvas. */}
                <div ref={register(id)} aria-hidden="true" className="pointer-events-none fixed -left-[9999px] top-0 isolate overflow-hidden" style={{
              width: w,
              height: h
            }} data-id="element-8153" data-mp="80tzlrgbpml4">
                  <EndCard ratio={ratio} data-id="element-8154" data-mp="i0kqzk9l7lgz" />
                </div>
              </section>;
        })}
        </div>
      </div>

      {/* ── Brand marks ──────────────────────────────────────────────────── */}
      <div className="mx-auto mt-20 max-w-[1360px] px-6" data-id="element-8155" data-mp="3lico1d2kyvm1">
        <div className="border-b border-[#E7E0D4] pb-3" data-id="element-8156" data-mp="3qfctpmvipwcm">
          <p className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-8157" data-mp="3qdrz5x97eder">
            Marks
          </p>
          <h2 className="font-title mt-1.5 text-[26px] font-semibold tracking-tight text-[#2B211A]" data-id="element-8158" data-mp="32k23a74m9wc">
            Giga, and everything that connects to it
          </h2>
          <p className="mt-2 max-w-[76ch] text-[13.5px] leading-relaxed text-[#6B5D52]" data-id="element-8159" data-mp="32o7glfgypi60">
            Giga's own marks on transparent artboards at 4×, since a mark gets
            placed small and scaled by whoever uses it. Everything Giga
            connects to follows underneath, as lockups.
          </p>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-8160" data-mp="2q7od3ta4gjms">
          {BRAND_ASSETS.map(mark => <div key={mark.id} className="rounded-2xl border border-[#E7E0D4] bg-white p-4" onPointerEnter={() => warm(mark.id)} data-id="element-8161" data-mp="2l8ldiocwv5m7">
              {/* A filled tile carries its own ground, so the checkerboard
               would only appear as a border around it and imply transparency
               the file does not have. Those sit on a plain well instead. */}
              <div className="flex items-center justify-center rounded-xl" style={{
            height: 168,
            backgroundColor: mark.filled ? '#F3EEE7' : '#FFFFFF',
            backgroundImage: mark.filled ? undefined : 'linear-gradient(45deg,#EFEAE1 25%,transparent 25%,transparent 75%,#EFEAE1 75%),linear-gradient(45deg,#EFEAE1 25%,transparent 25%,transparent 75%,#EFEAE1 75%)',
            backgroundSize: '14px 14px',
            backgroundPosition: '0 0, 7px 7px'
          }} data-id="element-8162" data-mp="26jyr62ov9dlh">
                <span ref={register(mark.id)} className="inline-flex items-center justify-center" style={{
              width: mark.boxW,
              height: mark.boxH
            }} data-id="element-8163" data-mp="1nwzkikv363yn">
                  {mark.render()}
                </span>
              </div>
              <p className="font-title mt-3 text-[15px] font-bold text-[#2B211A]" data-id="element-8164" data-mp="21mjjrgpgkj37">{mark.name}</p>
              <p className={`${EXPORT_META} mt-0.5`} data-id="element-8165" data-mp="26m5xrsd423d2">{mark.note}</p>
              <button type="button" onClick={() => savePng(mark.id, 4)} disabled={busy === mark.id} className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#D8CFC2] bg-white px-3 py-2 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:border-[#2B211A] disabled:opacity-60" data-id="element-8166" data-mp="26nqw7t2jq6g7">
                <Download size={13} data-id="element-8167" data-mp="2pcnmzmyqigto" />
                {busy === mark.id ? 'Preparing…' : 'PNG 4×'}
              </button>
            </div>)}
        </div>

      </div>

      {/* ── Lockups ──────────────────────────────────────────────────────────
       THE SAME GRID THE SCREEN LIBRARY USES.
       The pack shipped bare marks in a dense grid, which hands over a logo but
       not the thing anyone places on a clip: the mark WITH its name, in the
       tone the footage needs. This is the screen library's component, imported
       rather than copied — capsule or bare, for light or dark, PNG, SVG and
       Figma paste on every one.
       ──────────────────────────────────────────────────────────────────────*/}
      <div className="mx-auto mt-20 max-w-[1360px] space-y-14 px-6" data-id="element-8168" data-mp="400lv6mh1u7j">
        <SurfacePills heading="Tool lockups" title="Connected tool mark and name" note="Reach for these when a clip names a tool — “Giga reads your Salesforce” lands harder with the tool's own lockup on screen than with the words alone. Marks come from the shared brand registry, so they match the connections map exactly." items={TOOL_PILLS} data-id="element-8169" data-mp="3w2ibp35e9h62" />
        <SurfacePills heading="CRM lockups" title="CRM mark and name" note="The CRMs a viewer is most likely to already run. Use the one your audience uses; a clip that names their CRM is a clip about them." items={CRM_PILLS} data-id="element-8170" data-mp="26ok3o461mg5q" />
        <SurfacePills heading="Surface lockups" title="Platform mark and name" note="Where Giga answers — Slack, Teams, ChatGPT and the rest. The capsule is translucent, so whatever sits behind it still shows through." items={SURFACE_PILLS} data-id="element-8171" data-mp="2fq0ay31ka25q" />
      </div>
    </main>;
}